import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Banknote, CalendarRange, Users } from 'lucide-react';
import { DetailTableLayout } from '@/components/common/DetailTableLayout';
import { ExportButton } from '@/components/common/ExportButton';
import { LabeledPillList, type PillField } from '@/components/common/LabeledPill';
import { EMPTY_VALUE, TAKA_SYMBOL } from '@/constants/app.constants';
import { useRememberViewedRow } from '@/hooks/useLastViewedRow';
import { formatDisplayDate } from '@/utils/date';
import { formatNumber } from '@/utils/number';
import {
  batchMembersQueryOptions,
  batchQueryOptions,
  exportBatchMembers,
} from '../api/publish.api';
import { getBatchMemberColumns } from '../components/BatchColumns';
import { BatchStageBadge } from '../components/BatchStageBadge';
import { BatchTrail } from '../components/BatchTrail';
import type { Batch, BatchMembersSearch } from '../types/publish.types';
import { formatBatchAmount } from '../utils/formatBatchAmount';

const ARCHIVED_MEMBERS_MESSAGE = 'Member details are not kept for this batch';
const ARCHIVED_MEMBERS_HINT = 'This batch is too old. Only the totals above are kept.';

function formatCovering(batch: Batch): string {
  if (batch.trnDateFrom === null) return EMPTY_VALUE;
  return `${formatDisplayDate(batch.trnDateFrom)} – ${formatDisplayDate(batch.trnDateTo)}`;
}

function getBatchPills(batch: Batch | undefined, isLoading: boolean): PillField[] {
  return [
    {
      key: 'members',
      icon: Users,
      label: 'ZMs',
      value: batch === undefined ? undefined : formatNumber(batch.memberCount),
      isLoading,
    },
    {
      key: 'total',
      icon: Banknote,
      label: 'Total',
      value:
        batch === undefined ? undefined : `${TAKA_SYMBOL} ${formatBatchAmount(batch.totalAmount)}`,
      isLoading,
    },
    {
      key: 'covering',
      icon: CalendarRange,
      label: 'Covering',
      value: batch === undefined ? undefined : formatCovering(batch),
      isLoading,
    },
  ];
}

interface BatchMembersPageProps {
  batchId: number;
  search: BatchMembersSearch;
}

export function BatchMembersPage({ batchId, search }: BatchMembersPageProps) {
  const navigate = useNavigate();

  useRememberViewedRow('publishBatches', batchId);

  const batchQuery = useQuery(batchQueryOptions(batchId));
  const membersQuery = useQuery(batchMembersQueryOptions(batchId));
  const columns = useMemo(() => getBatchMemberColumns(), []);

  const batch = batchQuery.data ?? undefined;
  const isArchived = batch?.rowsArchived === true;
  const members = membersQuery.data ?? [];

  return (
    <DetailTableLayout
      title={batch === undefined ? undefined : `Batch #${String(batch.legacyId)}`}
      titleAddon={batch === undefined ? null : <BatchStageBadge stage={batch.stage} />}
      actions={
        <ExportButton
          onExport={() => exportBatchMembers(batchId)}
          disabled={members.length === 0}
        />
      }
      onNoHistory={() => void navigate({ to: search.from })}
      summary={
        <>
          <LabeledPillList fields={getBatchPills(batch, batchQuery.isLoading)} />
          <BatchTrail batch={batch} />
        </>
      }
      table={{
        rows: members,
        columns,
        isLoading: membersQuery.isLoading,
        error: membersQuery.error ?? batchQuery.error,
        errorTitle: 'Could not load the ZMs on this batch',
        onRetry: () => {
          void batchQuery.refetch();
          void membersQuery.refetch();
        },
        stickyColumnId: 'member',
        emptyMessage: isArchived ? ARCHIVED_MEMBERS_MESSAGE : 'No ZM is on this batch',
        emptyHint: isArchived ? ARCHIVED_MEMBERS_HINT : undefined,
      }}
    />
  );
}
